import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';

type ResolvedTheme = 'dark' | 'light';

/// User-facing theme preference. `'system'` follows the OS-level
/// `prefers-color-scheme` and live-updates when the OS preference
/// changes. `'dark'` / `'light'` pin the app to that mode regardless
/// of OS. Mirrors Flutter mobile's `ThemeMode.{dark,light,system}`.
type ThemePreference = 'dark' | 'light' | 'system';

interface ThemeContextValue {
  /// The resolved theme actually applied to `<html>`. Always
  /// `'dark'` or `'light'` — even when [themePreference] is `'system'`,
  /// this carries the currently-resolved value so callers branching
  /// on light vs dark don't have to peek at `matchMedia` themselves.
  theme: ResolvedTheme;
  /// Persisted user preference. `'system'` is the default for fresh
  /// installs.
  themePreference: ThemePreference;
  /// Accepts the broader preference (including `'system'`). Persists
  /// to localStorage.
  setTheme: (pref: ThemePreference) => void;
  /// Cycles between dark and light explicitly — drops `'system'` mode
  /// because a one-tap toggle implies "I want THIS specifically." UI
  /// that wants `system` should call `setTheme('system')` directly.
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'theme';

function getInitialPreference(): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system';
  }
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'dark' || saved === 'light' || saved === 'system') {
    return saved;
  }
  // Migration: prior versions persisted only 'dark' | 'light' and
  // never wrote 'system' — treat anything else as the new default.
  return 'system';
}

function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  return pref === 'system' ? resolveSystemTheme() : pref;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setPreferenceState] = useState<ThemePreference>(getInitialPreference);
  const [theme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(getInitialPreference())
  );

  // Persist the preference + recompute the resolved theme whenever the
  // preference changes.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, themePreference);
    setResolvedTheme(resolveTheme(themePreference));
  }, [themePreference]);

  // Live-update on OS-level changes while preference is 'system'. The
  // matchMedia listener is a no-op for the dark/light branches because
  // the resolved theme is already pinned.
  useEffect(() => {
    if (themePreference !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setResolvedTheme(e.matches ? 'dark' : 'light');
    };
    // Safari ≤ 13 only supports the legacy `addListener` API; modern
    // browsers prefer `addEventListener('change', …)`. Pick whichever
    // is available — both deliver the same `MediaQueryListEvent`.
    if ('addEventListener' in mq) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    return undefined;
  }, [themePreference]);

  // Reflect the resolved theme onto `<html>` so Tailwind's
  // `dark:` variant + CSS-custom-properties scoped under `.dark` /
  // `.light` switch in lockstep.
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  const setTheme = (pref: ThemePreference) => {
    setPreferenceState(pref);
  };

  const toggleTheme = () => {
    // Toggle drops 'system' mode — the user explicitly opted into a
    // specific shade. To re-enable system tracking they pick it from
    // the settings dialog.
    setPreferenceState((prev) => {
      const current = prev === 'system' ? resolveSystemTheme() : prev;
      return current === 'dark' ? 'light' : 'dark';
    });
  };

  const contextValue = useMemo(
    () => ({ theme, themePreference, setTheme, toggleTheme }),
    [theme, themePreference]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

// Fast refresh: hook export is intentional
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
